import React, { useState } from 'react';
import {
  useGetDevelopmentsQuery,
  useGetUnitsQuery,
} from "../../../redux/vmsApi";

import {
  Box, Button, FormControl, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Paper
} from '@mui/material';


const UnitsList: React.FC = () => {
  const { data: developments = [] } = useGetDevelopmentsQuery();
  const [selectedDevelopment, setSelectedDevelopment] = useState('');
  const [selectedProject, setSelectedProject] = useState('');

  const projects = selectedDevelopment
    ? developments.find((dev: any) => dev.id === selectedDevelopment)?.projects || []
    : [];

  const handleDevelopmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const devId = e.target.value;
    setSelectedDevelopment(devId);
    setSelectedProject('');
    setFilters(prev => ({ ...prev, developmentId: devId, projectId: '' }));
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const projId = e.target.value;
    setSelectedProject(projId);
    setFilters(prev => ({ ...prev, projectId: projId }));
  };
  const [filters, setFilters] = useState({
    unitName: '',
    developmentId: '',
    projectId: '',
  });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });

  const { data: units, isLoading, isError } = useGetUnitsQuery(apiParams);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setApiParams({ ...filters, skip: 0, limit: pageSize });
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setApiParams(prev => ({ ...prev, skip: newPage * pageSize }));
  };

  const handlePageSizeChange = (event: any) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    setApiParams(prev => ({ ...prev, limit: newSize, skip: 0 }));
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading units.</div>;

  return (
    <React.Fragment>
      <Box sx={{ padding: 3, borderRadius: 2, marginBottom: 3, backgroundColor: '#f9f9f9', boxShadow: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Filter Units
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <TextField
            label="Unit Name"
            variant="outlined"
            size="small"
            value={filters.unitName}
            onChange={(e) => handleFilterChange('unitName', e.target.value)}
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            fullWidth
          />
          <TextField
            select
            label="Development"
            variant="outlined"
            size="small"
            value={selectedDevelopment}
            onChange={handleDevelopmentChange}
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            fullWidth
          >
            {developments.map(dev => (
              <MenuItem key={dev.id} value={dev.id}>
                {dev.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Project"
            variant="outlined"
            size="small"
            value={selectedProject}
            onChange={handleProjectChange}
            disabled={!selectedDevelopment}
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            fullWidth
          >
            {projects.map(proj => (
              <MenuItem key={proj.id} value={proj.id}>
                {proj.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Stack direction="row" spacing={2} mt={2}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleApplyFilters}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#1565c0' },
            }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unit Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Development</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Project</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {units && units.length > 0 ? (
              units.map((unit: any, index: number) => (
                <TableRow key={unit.id} sx={{ backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                  <TableCell>{unit.unitName}</TableCell>
                  <TableCell>{unit.development?.name}</TableCell>
                  <TableCell>{unit.project?.name}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">No matching units found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button onClick={() => handlePageChange(Math.max(page - 1, 0))} disabled={page === 0} variant="contained" sx={{ marginRight: 2 }}>
          Previous
        </Button>
        <Typography variant="body1">Page {page + 1}</Typography>
        <Button onClick={() => handlePageChange(page + 1)} disabled={units?.length === undefined || units.length < pageSize} variant="contained" sx={{ marginLeft: 2 }}>
          Next
        </Button>
      </Box>

      <Box mt={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2" sx={{ marginRight: 2 }}>Rows per page:</Typography>
        <FormControl variant="outlined" size="small">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{ backgroundColor: '#f0f4f7' }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </React.Fragment>
  );
};

export default UnitsList;

